/**
 * The Stores masters (seedtrac STORES' Classification, Item and Party masters, and its units).
 * Stores vendors are not here: they are Vendors with "Stores vendor" switched on.
 */
import {
  masters,
  STORES_PARTY_CATEGORIES,
  storesPartyCategoryLabel,
} from "@nexseed/shared";
import { Layers, PackageSearch, Ruler, Store } from "lucide-react";
import { text } from "@/lib/text";
import { defineMaster, type MasterRow } from "../spec";

const status = { id: "isActive", header: "Status", kind: "status" } as const;
const activeSwitch = {
  name: "isActive",
  label: "Active",
  type: "switch",
} as const;

export const storesUoms = defineMaster({
  key: "stores-uoms",
  title: "Stores units",
  singular: "Unit",
  description:
    "The units a Stores item is kept in (Kg, Number, Litres…). Decimals is how finely its quantity is typed: Number takes whole units, Kg three places.",
  icon: Ruler,
  schema: masters.storesUom.create,
  searchPlaceholder: "Search unit",
  defaultSort: "name",
  optionLabel: (r: MasterRow) => String(r.name),
  columns: [
    { id: "code", header: "Code", kind: "mono", sortable: true },
    { id: "name", header: "Name", sortable: true },
    { id: "decimals", header: "Decimals", kind: "number", align: "right" },
    status,
  ],
  fields: [
    { name: "code", label: "Code", type: "text", required: true },
    { name: "name", label: "Name", type: "text", required: true },
    {
      name: "decimals",
      label: "Decimals",
      type: "number",
      min: 0,
      step: 1,
      required: true,
      description: "0 for whole units, up to 3",
    },
    activeSwitch,
  ],
});

export const storesClassifications = defineMaster({
  key: "stores-classifications",
  title: "Stores classifications",
  singular: "Classification",
  description:
    "How Stores items are grouped: Sticker Chilli, Roll Pumpkin, Pouch Bhindi, PP Bags, Chemicals…",
  icon: Layers,
  schema: masters.storesClassification.create,
  searchPlaceholder: "Search classification",
  defaultSort: "name",
  importer: {
    path: "/stores/import/classifications",
    help: "One classification per row. Classifications already in the master (same name) are left as they are.",
    columns: [
      {
        name: "classification",
        required: true,
        note: "The classification name, e.g. Sticker Chilli, Roll Coriander, PP Bags.",
      },
      {
        name: "status",
        note: "Active or Inactive. Blank means Active.",
      },
    ],
    sample: [
      ["Sticker Chilli", "Active"],
      ["Roll Coriander", ""],
      ["Pouch Bhindi", ""],
    ],
    invalidates: ["stores-classifications"],
  },
  columns: [{ id: "name", header: "Name", sortable: true }, status],
  fields: [
    { name: "name", label: "Name", type: "text", required: true, colSpan: 2 },
    activeSwitch,
  ],
});

export const storesItems = defineMaster({
  key: "stores-items",
  title: "Stores items",
  singular: "Stores item",
  description:
    "Every item the store keeps, under its classification, with its unit and an optional re-order level.",
  icon: PackageSearch,
  schema: masters.storesItem.create,
  importer: {
    path: "/stores/import/items",
    help: "One item per row. A classification not yet in the master is created; items already under that classification (same name) are left as they are.",
    columns: [
      {
        name: "classification",
        required: true,
        note: "The item's classification. A new name creates the classification.",
      },
      {
        name: "item",
        required: true,
        note: "The Stores item name, as printed on the GRN.",
      },
      {
        name: "uom",
        required: true,
        note: "The unit's code or name, already in Stores units (Kg, Nos, Litres…).",
      },
      {
        name: "reorder_level",
        note: "The Qty to re-order at. Blank means no re-order level.",
      },
    ],
    sample: [
      ["Sticker Chilli", "STK-VNR 446", "Nos", ""],
      ["Roll Sponge Gourd", "ROL-SPG Anita Medium (125mm)", "Kg", "25"],
    ],
    invalidates: ["stores-items", "stores-classifications"],
  },
  searchPlaceholder: "Search item",
  defaultSort: "name",
  optionLabel: (r: MasterRow) => String(r.name),
  optionHint: (r: MasterRow) => text(r.classificationName) || undefined,
  columns: [
    { id: "name", header: "Item", sortable: true },
    {
      id: "classificationId",
      header: "Classification",
      cell: (r) => text(r.classificationName) || "—",
    },
    {
      id: "uomId",
      header: "Unit",
      kind: "reference",
      reference: "stores-uoms",
    },
    {
      id: "reorderLevel",
      header: "Re-order level",
      align: "right",
      cell: (r) => (r.reorderEnabled ? text(r.reorderLevel) || "—" : "—"),
    },
    status,
  ],
  fields: [
    {
      name: "classificationId",
      label: "Classification",
      type: "reference",
      master: "stores-classifications",
      required: true,
    },
    {
      name: "uomId",
      label: "Unit of measurement",
      type: "reference",
      master: "stores-uoms",
      required: true,
    },
    {
      name: "name",
      label: "Stores item",
      type: "text",
      required: true,
      colSpan: 2,
    },
    { name: "reorderEnabled", label: "Re-order level", type: "switch" },
    {
      name: "reorderLevel",
      label: "Re-order at (Qty)",
      type: "number",
      min: 0,
      step: 0.001,
      showIf: (v) => v.reorderEnabled === true,
    },
    activeSwitch,
  ],
});

const CATEGORY_OPTIONS = STORES_PARTY_CATEGORIES.map((c) => ({
  value: c.value,
  label: c.label,
}));

export const storesParties = defineMaster({
  key: "stores-parties",
  title: "Stores parties",
  singular: "Party",
  description:
    "Parties the store deals with that are neither vendors nor our plants: C&F agents and dealers, transfer points that are not on NexSeed, and the own units an Internal Return comes back from. Vendors are kept in the Vendor master; our plants come from Plants.",
  icon: Store,
  schema: masters.storesParty.create,
  importer: {
    path: "/stores/import/parties",
    help: "One party per row. Parties already in the master (same name) are left as they are. Vendors go in Stores ▸ Vendors, not here.",
    columns: [
      {
        name: "category",
        required: true,
        note: "C&F, Dealer, Other, Stock Transfer (a transfer point not on NexSeed) or Own unit.",
      },
      { name: "name", required: true, note: "The party's name." },
      { name: "contact_person", note: "Who to ask for." },
      {
        name: "address",
        note: "Street, area. Required for C&F, Dealer and Other.",
      },
      {
        name: "city",
        note: "City, town or village. Required for C&F, Dealer and Other.",
      },
      { name: "pincode", note: "6-digit PIN." },
      {
        name: "state",
        note: "The state, spelt out (Bihar). Required for C&F, Dealer and Other.",
      },
      { name: "country", note: "Blank means India." },
      { name: "phone", note: "Landline." },
      { name: "mobile", note: "10-digit mobile." },
      { name: "gstin", note: "15-character GSTIN." },
      { name: "pan", note: "10-character PAN." },
    ],
    sample: [
      [
        "C&F",
        "VNR Seeds Pvt Ltd - Patna (C&F)",
        "",
        "Industrial Area",
        "Patna",
        "800001",
        "Bihar",
        "",
        "",
        "",
        "",
        "",
      ],
    ],
    invalidates: ["stores-parties"],
  },
  searchPlaceholder: "Search name or city",
  defaultSort: "name",
  formSize: "lg",
  optionLabel: (r: MasterRow) => String(r.name),
  optionHint: (r: MasterRow) => storesPartyCategoryLabel(String(r.category)),
  columns: [
    { id: "name", header: "Name", sortable: true },
    {
      id: "category",
      header: "Category",
      cell: (r) => storesPartyCategoryLabel(String(r.category)) || "—",
    },
    { id: "city", header: "City", sortable: true },
    { id: "state", header: "State" },
    status,
  ],
  fields: [
    {
      name: "category",
      label: "Category",
      type: "enum",
      required: true,
      options: CATEGORY_OPTIONS,
    },
    { name: "name", label: "Party name", type: "text", required: true },
    { name: "contactPerson", label: "Contact person", type: "text" },
    { name: "mobile", label: "Mobile", type: "phone" },
    // Required of a C&F agent, dealer or other party (the schema says so and names the box); a
    // Stock Transfer party or Own unit is named as seedtrac lists it, with no address.
    {
      name: "address",
      label: "Address",
      type: "textarea",
      colSpan: 2,
    },
    {
      name: "city",
      label: "City / Town / Village",
      type: "text",
    },
    { name: "pincode", label: "PIN code", type: "text" },
    { name: "state", label: "State", type: "text" },
    { name: "country", label: "Country", type: "text" },
    { name: "phone", label: "Phone", type: "text" },
    { name: "gstNumber", label: "GSTIN", type: "text", transform: "upper" },
    { name: "panNumber", label: "PAN", type: "text", transform: "upper" },
    activeSwitch,
  ],
});
